// import { Editor } from '@tinymce/tinymce-react';
import { ForwardedRef, Suspense, forwardRef, lazy } from 'react';
import { getMIMEFromFileName } from '@vindr/utils/converter';
import resizeBase64Image from '@vindr/utils/resizeBlobImage';
import Loading from './Loading';

const Editor = lazy(() =>
  import('@tinymce/tinymce-react').then((module) => ({
    default: module.Editor,
  }))
);

interface TextEditorProps {
  defaultValue?: string;
  value?: string;
  disabled?: boolean;
  height?: number;
  menubar?: boolean;
  onChange?: (value: string) => void;
  resize?: boolean;
  onKeyDown?: (value: string) => void;
}

const TextEditor = forwardRef(
  (
    {
      defaultValue,
      value,
      disabled,
      height = 500,
      menubar = true,
      onChange,
      resize = true,
      onKeyDown,
    }: TextEditorProps,
    ref: ForwardedRef<typeof Editor>
  ) => {
    return (
      <article
        className="rounded-[10px] bg-white"
        {...(height && !resize && { style: { height } })}
      >
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center">
              <Loading />
            </div>
          }
        >
          <Editor
            ref={ref}
            tinymceScriptSrc={'/tinymce/tinymce.min.js'} // https://www.tiny.cloud/docs/tinymce/6/react-pm-host/
            initialValue={defaultValue}
            value={value}
            onEditorChange={onChange}
            disabled={disabled}
            onKeyDown={onKeyDown}
            init={{
              menubar,
              height,
              resize,
              images_upload_handler: async (blobInfo) => {
                const type = getMIMEFromFileName(blobInfo.filename());
                const url = (await `data:${type};base64,`) + blobInfo.base64();

                return await resizeBase64Image(url, { type });
              },
              content_style:
                'body { font-family: "Times New Roman", Times, serif; } img { max-width: 100%; } table:not([cellpadding]) td, table:not([cellpadding]) th { padding: 0 !important; }',
              plugins: ['lists', 'table'],
              toolbar:
                'undo redo | blocks | ' +
                'bold italic forecolor | alignleft aligncenter ' +
                'alignright alignjustify | bullist numlist outdent indent | ' +
                'table | removeformat',
            }}
          />
        </Suspense>
      </article>
    );
  }
);

export default TextEditor;
